/**
 * FE-PR-20 — sign-in screen view (presentational).
 */
import { Link } from 'react-router-dom';
import { TurnstileExecuteWidget } from 'pi-kiosk-shared/ui';
import { CUSTOMER_FORM_DIRTY_ATTR } from '../../../app/pwa/customerFormDirtyGate.js';
import { Card } from '../../../shared/ui/Card.js';
import { Button } from '../../../shared/ui/Button.js';
import { FormField } from '../../../shared/ui/FormField.js';
import {
  FormErrorSummary,
  mapFieldErrorsToSummary,
  type FormErrorSummaryItem,
} from '../../../shared/ui/FormErrorSummary.js';
import { AuthDeliveryErrorPanel } from '../AuthDeliveryErrorPanel.js';
import { DEFAULT_REGISTRATION_CONSENTS } from '../authConsents.js';
import { RegistrationConsentsField } from '../RegistrationConsentsField.js';
import { RememberMeField } from '../RememberMeField.js';
import { grantFunctionalForRememberMe } from '../grantFunctionalForRememberMe.js';
import type { SignInScreenViewProps } from '../hooks/useSignInScreen.js';
import { resolveCustomerSignUpPath, resolveCustomerSetCredentialsPath, resolveCustomerForgotPasswordPath } from '../customerAuthPaths.js';
import { CUSTOMER_SIGN_IN_FIELD_IDS } from '../customerAuthFieldIds.js';
import { PLATFORM_ACCOUNT_SHOP_PATH, PLATFORM_SHOPS_PATH } from '../../shop/resolveShopHref.js';
import {
  CUSTOMER_CONTENT_AUTH_CLASS,
  CUSTOMER_AUTH_PRIMARY_CTA_MAX_CLASS,
  CUSTOMER_AUTH_METHOD_TOGGLE_CLASS,
  CUSTOMER_AUTH_FIELD_CLASS,
} from '../../../app/customerTopBarStyles.js';
import { OidcSocialButtons } from './OidcSocialButtons.js';
import { AuthFormErrorBanner } from './AuthFormErrorBanner.js';

export type { SignInScreenViewProps };

const SIGN_IN_CONSENT_SUMMARY_IDS = {
  'consents.termsAccepted': CUSTOMER_SIGN_IN_FIELD_IDS.terms,
  'consents.privacyAccepted': CUSTOMER_SIGN_IN_FIELD_IDS.privacy,
} as const;

function EmailNotVerifiedHint(props: {
  readonly t: SignInScreenViewProps['t'];
  readonly onGoToPending: () => void;
}): JSX.Element {
  const { t, onGoToPending } = props;
  return (
    <p
      className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
      role="status"
      data-testid="customer-sign-in-email-not-verified"
    >
      {t('auth.emailNotVerifiedHint')}{' '}
      <button
        type="button"
        className="text-[var(--color-accent)] underline"
        onClick={onGoToPending}
      >
        {t('auth.pendingVerificationLink')}
      </button>
    </p>
  );
}

function renderTurnstileExecuteField(
  turnstile: SignInScreenViewProps['turnstile'],
  testId?: string,
): JSX.Element | null {
  if (!turnstile.required) {
    return null;
  }
  return (
    <TurnstileExecuteWidget
      turnstile={turnstile}
      testId={testId ?? 'customer-turnstile-execute-field'}
    />
  );
}

function buildPasswordFormSummary(
  identifierError: string | undefined,
  passwordError: string | undefined,
): FormErrorSummaryItem[] {
  // G3 — password login never mounts consent ticks / consent summary rows.
  return mapFieldErrorsToSummary(
    {
      identifier: identifierError,
      password: passwordError,
    },
    {
      identifier: CUSTOMER_SIGN_IN_FIELD_IDS.identifier,
      password: CUSTOMER_SIGN_IN_FIELD_IDS.password,
    },
  );
}

function buildOtpRequestSummary(
  identifierError: string | undefined,
): FormErrorSummaryItem[] {
  return mapFieldErrorsToSummary(
    { identifier: identifierError },
    { identifier: CUSTOMER_SIGN_IN_FIELD_IDS.identifier },
  );
}

function buildOtpVerifySummary(
  codeError: string | undefined,
  consentFieldErrors: Record<string, string>,
  showOtpConsents: boolean,
): FormErrorSummaryItem[] {
  const fieldErrors: Record<string, string | undefined> = {
    code: codeError,
  };
  const ids: Record<string, string> = {
    code: CUSTOMER_SIGN_IN_FIELD_IDS.code,
  };
  if (showOtpConsents) {
    return [
      ...mapFieldErrorsToSummary(fieldErrors, ids),
      ...mapFieldErrorsToSummary(consentFieldErrors, SIGN_IN_CONSENT_SUMMARY_IDS),
    ];
  }
  return mapFieldErrorsToSummary(fieldErrors, ids);
}

function SignInFormErrorBanner(props: {
  readonly formError: string | undefined;
}): JSX.Element | null {
  return (
    <AuthFormErrorBanner
      message={props.formError}
      testId="customer-sign-in-form-error"
    />
  );
}

function renderMainForm(props: SignInScreenViewProps): JSX.Element {
  const {
    t,
    tenantCode,
    authMethod,
    identifier,
    setIdentifier,
    password,
    setPassword,
    code,
    setCode,
    rememberMe,
    setRememberMe,
    showRememberMe,
    identifierError,
    passwordError,
    codeError,
    formError,
    deliveryErrorCode,
    credentialsNotConfigured,
    emailNotVerified,
    showOtpConsents,
    otpConsents,
    setOtpConsents,
    consentFieldErrors,
    challenge,
    consent,
    turnstile,
    showPageTurnstile,
    requestOtp,
    verifyOtp,
    loginPassword,
    handlePasswordLogin,
    handleRequest,
    handleVerify,
    goToPendingVerification,
    resetOtpChallenge,
    switchToOtpTab,
    setCredentialsNotConfigured,
    setPasswordError,
    identifierLabel,
    identifierPlaceholder,
    identifierInputMode,
    passwordIdentifierLabel,
    passwordIdentifierPlaceholder,
  } = props;

  const isDraftDirty =
    identifier.trim().length > 0 ||
    password.trim().length > 0 ||
    code.trim().length > 0 ||
    otpConsents.termsAccepted !== DEFAULT_REGISTRATION_CONSENTS.termsAccepted ||
    otpConsents.privacyAccepted !== DEFAULT_REGISTRATION_CONSENTS.privacyAccepted ||
    otpConsents.marketing !== DEFAULT_REGISTRATION_CONSENTS.marketing ||
    otpConsents.analytics !== DEFAULT_REGISTRATION_CONSENTS.analytics;

  if (authMethod === 'password') {
    const summaryErrors = buildPasswordFormSummary(identifierError, passwordError);
    return (
      <>
        <form
          className="flex flex-col gap-[var(--vcl-space-stack-lg)]"
          onSubmit={(event) => void handlePasswordLogin(event)}
          noValidate
          {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
        >
          <FormErrorSummary errors={summaryErrors} />
          <div className={CUSTOMER_AUTH_FIELD_CLASS}>
            <FormField
              id={CUSTOMER_SIGN_IN_FIELD_IDS.identifier}
              label={passwordIdentifierLabel}
              placeholder={passwordIdentifierPlaceholder}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              type="text"
              inputMode={identifierInputMode}
              autoComplete="username"
              invalid={Boolean(identifierError)}
              disabled={loginPassword.isPending}
              data-testid="customer-sign-in-identifier"
            />
          </div>
          <div className={CUSTOMER_AUTH_FIELD_CLASS}>
            <FormField
              id={CUSTOMER_SIGN_IN_FIELD_IDS.password}
              label={t('auth.passwordLabel')}
              type="password"
              placeholder={t('auth.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              invalid={Boolean(passwordError)}
              disabled={loginPassword.isPending}
              data-testid="customer-sign-in-password"
            />
          </div>
          {credentialsNotConfigured ? (
            <div
              className="rounded-[var(--vcl-control-radius-md)] border border-[var(--color-border)] p-3 text-[length:var(--vcl-type-body-sm-size)]"
              role="status"
              data-testid="customer-credentials-not-configured"
            >
              <p className="text-[var(--color-on-surface-muted)]">
                {t('auth.credentialsNotConfiguredBody')}
              </p>
              <p className="mt-2 text-[var(--color-on-surface-muted)]">
                {t('auth.credentialsNotConfiguredOtpFirst')}
              </p>
              <div className="mt-3 flex flex-col gap-[var(--vcl-space-stack-sm)]">
                <Button
                  type="button"
                  intent="secondary"
                  size="md"
                  block
                  onClick={() => {
                    switchToOtpTab();
                    setCredentialsNotConfigured(false);
                    setPasswordError(undefined);
                  }}
                  data-testid="customer-sign-in-use-otp"
                >
                  {t('auth.useOtpInstead')}
                </Button>
                <Link
                  to={resolveCustomerSetCredentialsPath(tenantCode)}
                  className="text-center text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-accent)] underline"
                  data-testid="customer-sign-in-set-credentials-link"
                >
                  {t('auth.setCredentialsLink')}
                </Link>
              </div>
            </div>
          ) : null}
          {emailNotVerified ? (
            <EmailNotVerifiedHint t={t} onGoToPending={goToPendingVerification} />
          ) : null}
          {showRememberMe ? (
            <RememberMeField
              checked={rememberMe}
              onChange={setRememberMe}
              onEnablePersistence={() => {
                grantFunctionalForRememberMe(consent.enableLoginPersistence);
              }}
              disabled={loginPassword.isPending}
            />
          ) : null}
          <SignInFormErrorBanner formError={formError} />
          {renderTurnstileExecuteField(turnstile)}
          <Button
            type="submit"
            block
            size="lg"
            disabled={loginPassword.isPending}
            data-testid="customer-sign-in-password-submit"
            className={`${CUSTOMER_AUTH_PRIMARY_CTA_MAX_CLASS}`}
          >
            {loginPassword.isPending ? t('auth.signingIn') : t('auth.signInWithPassword')}
          </Button>
        </form>
        <p className="mt-4 text-center text-[length:var(--vcl-type-body-sm-size)]">
          <Link
            to={resolveCustomerForgotPasswordPath(tenantCode)}
            className="text-[var(--color-accent)] underline"
            data-testid="customer-sign-in-forgot-password"
          >
            {t('auth.forgotPasswordLink')}
          </Link>
        </p>
      </>
    );
  }

  if (challenge === null) {
    const summaryErrors = buildOtpRequestSummary(identifierError);
    return (
      <form
        className="flex flex-col gap-[var(--vcl-space-stack-lg)]"
        onSubmit={(event) => void handleRequest(event)}
        noValidate
        {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
      >
        <FormErrorSummary errors={summaryErrors} />
        <div className={CUSTOMER_AUTH_FIELD_CLASS}>
          <FormField
            id={CUSTOMER_SIGN_IN_FIELD_IDS.identifier}
            label={identifierLabel}
            placeholder={identifierPlaceholder}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            type="text"
            inputMode={identifierInputMode}
            autoComplete="username"
            invalid={Boolean(identifierError)}
            disabled={requestOtp.isPending}
            data-testid="customer-sign-in-identifier"
          />
        </div>
        {deliveryErrorCode !== undefined ? (
          <AuthDeliveryErrorPanel code={deliveryErrorCode} />
        ) : null}
        {emailNotVerified ? (
          <EmailNotVerifiedHint t={t} onGoToPending={goToPendingVerification} />
        ) : null}
        <SignInFormErrorBanner formError={formError} />
        {showPageTurnstile ? null : renderTurnstileExecuteField(turnstile)}
        <Button
          type="submit"
          block
          size="lg"
          disabled={requestOtp.isPending}
          data-testid="customer-sign-in-send-code"
        >
          {requestOtp.isPending ? t('auth.sendingCode') : t('auth.sendCode')}
        </Button>
      </form>
    );
  }

  const verifySummaryErrors = buildOtpVerifySummary(
    codeError,
    consentFieldErrors,
    showOtpConsents,
  );
  return (
    <form
      className="flex flex-col gap-[var(--vcl-space-stack-lg)]"
      onSubmit={(event) => void handleVerify(event)}
      noValidate
      {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
    >
      <FormErrorSummary errors={verifySummaryErrors} />
      <div className={CUSTOMER_AUTH_FIELD_CLASS}>
        <FormField
          id={CUSTOMER_SIGN_IN_FIELD_IDS.code}
          label={t('auth.codeLabel')}
          placeholder={t('auth.codePlaceholder')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          invalid={Boolean(codeError)}
          disabled={verifyOtp.isPending}
          data-testid="customer-sign-in-code"
        />
      </div>
      {emailNotVerified ? (
        <EmailNotVerifiedHint t={t} onGoToPending={goToPendingVerification} />
      ) : null}
      {showOtpConsents ? (
        <div data-testid="customer-sign-in-otp-consents">
          <RegistrationConsentsField
            value={otpConsents}
            onChange={setOtpConsents}
            disabled={verifyOtp.isPending}
            includeOptional={false}
            termsInputId={CUSTOMER_SIGN_IN_FIELD_IDS.terms}
            privacyInputId={CUSTOMER_SIGN_IN_FIELD_IDS.privacy}
            termsError={consentFieldErrors['consents.termsAccepted']}
            privacyError={consentFieldErrors['consents.privacyAccepted']}
          />
        </div>
      ) : null}
      {showRememberMe ? (
        <RememberMeField
          checked={rememberMe}
          onChange={setRememberMe}
          onEnablePersistence={() => {
            grantFunctionalForRememberMe(consent.enableLoginPersistence);
          }}
          disabled={verifyOtp.isPending}
        />
      ) : null}
      <SignInFormErrorBanner formError={formError} />
      {renderTurnstileExecuteField(turnstile)}
      <div className="flex flex-col gap-[var(--vcl-space-stack-sm)]">
        <Button
          type="submit"
          block
          size="lg"
          disabled={verifyOtp.isPending}
          data-testid="customer-sign-in-verify"
        >
          {verifyOtp.isPending ? t('auth.verifying') : t('auth.verifyAndContinue')}
        </Button>
        <Button
          type="button"
          intent="ghost"
          block
          size="md"
          onClick={resetOtpChallenge}
          disabled={verifyOtp.isPending}
        >
          {t('auth.enterOtherContact')}
        </Button>
      </div>
    </form>
  );
}

export function SignInScreenView(props: SignInScreenViewProps): JSX.Element {
  const {
    t,
    tenantCode,
    resolvedHeading,
    channelHint,
    currentTenantProspect,
    postOnboardingHint,
    authMethod,
    turnstile,
    socialAuth,
    showPageTurnstile,
    showOidcStartConsents,
    otpConsents,
    setOtpConsents,
    consentFieldErrors,
    requestOtp,
    verifyOtp,
    loginPassword,
    handleOidcLogin,
    switchToOtpTab,
    switchToPasswordTab,
    oidcRecoveryMessage,
    accountLocked,
    accountLockedUntil,
  } = props;

  const oidcConsentSummary = showOidcStartConsents
    ? mapFieldErrorsToSummary(consentFieldErrors, SIGN_IN_CONSENT_SUMMARY_IDS)
    : [];

  return (
    <div className={`flex min-w-0 flex-col gap-[var(--vcl-space-stack-lg)] ${CUSTOMER_CONTENT_AUTH_CLASS}`}>
      <Card padded data-testid="customer-sign-in-screen">
        <header className="mb-4">
          <h1 className="text-xl font-semibold text-[var(--color-on-surface)]">
            {resolvedHeading}
          </h1>
          <p className="text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">{channelHint}</p>
        </header>

        {postOnboardingHint === 'credentialsSet' ? (
          <div
            className="mb-3 rounded-[var(--vcl-control-radius-md)] border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 p-3"
            role="status"
            data-testid="customer-sign-in-post-onboarding-credentials"
          >
            <p className="text-[length:var(--vcl-type-body-sm-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]">
              {t('auth.postOnboardingCredentialsSetTitle')}
            </p>
            <p className="mt-1 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {t('auth.postOnboardingCredentialsSetBody')}
            </p>
            <div className="mt-3 flex flex-wrap gap-[var(--vcl-gap-inline-md)] text-[length:var(--vcl-type-body-sm-size)]">
              <Link
                to={PLATFORM_SHOPS_PATH}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-post-onboarding-credentials-shops"
              >
                {t('auth.noActiveMembershipShopsCta')}
              </Link>
              <Link
                to={PLATFORM_ACCOUNT_SHOP_PATH}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-post-onboarding-credentials-account-shop"
              >
                {t('auth.noActiveMembershipAccountShopCta')}
              </Link>
              <Link
                to={resolveCustomerSignUpPath(tenantCode)}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-post-onboarding-credentials-register"
              >
                {t('auth.noActiveMembershipRegisterCta')}
              </Link>
            </div>
          </div>
        ) : null}

        {postOnboardingHint === 'emailVerified' ? (
          <div
            className="mb-3 rounded-[var(--vcl-control-radius-md)] border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 p-3"
            role="status"
            data-testid="customer-sign-in-post-onboarding-email"
          >
            <p className="text-[length:var(--vcl-type-body-sm-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]">
              {t('auth.postOnboardingEmailVerifiedTitle')}
            </p>
            <p className="mt-1 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {t('auth.postOnboardingEmailVerifiedBody')}
            </p>
            <div className="mt-3 flex flex-wrap gap-[var(--vcl-gap-inline-md)] text-[length:var(--vcl-type-body-sm-size)]">
              <Link
                to={PLATFORM_SHOPS_PATH}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-post-onboarding-email-shops"
              >
                {t('auth.noActiveMembershipShopsCta')}
              </Link>
              <Link
                to={PLATFORM_ACCOUNT_SHOP_PATH}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-post-onboarding-email-account-shop"
              >
                {t('auth.noActiveMembershipAccountShopCta')}
              </Link>
              <Link
                to={resolveCustomerSignUpPath(tenantCode)}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-post-onboarding-email-register"
              >
                {t('auth.noActiveMembershipRegisterCta')}
              </Link>
            </div>
          </div>
        ) : null}

        {postOnboardingHint === 'oidcPlatformRegistered' ? (
          <div
            className="mb-3 rounded-[var(--vcl-control-radius-md)] border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 p-3"
            role="status"
            data-testid="customer-sign-in-post-onboarding-oidc"
          >
            <p className="text-[length:var(--vcl-type-body-sm-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]">
              {t('auth.postOnboardingOidcRegisteredTitle')}
            </p>
            <p className="mt-1 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
              {t('auth.postOnboardingOidcRegisteredBody')}
            </p>
            <div className="mt-3 flex flex-wrap gap-[var(--vcl-gap-inline-md)] text-[length:var(--vcl-type-body-sm-size)]">
              <Link
                to={PLATFORM_SHOPS_PATH}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-oidc-registered-shops"
              >
                {t('auth.noActiveMembershipShopsCta')}
              </Link>
              <Link
                to={PLATFORM_ACCOUNT_SHOP_PATH}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-oidc-registered-account-shop"
              >
                {t('auth.noActiveMembershipAccountShopCta')}
              </Link>
              <Link
                to={resolveCustomerSignUpPath(tenantCode)}
                className="text-[var(--color-accent)] underline"
                data-testid="customer-sign-in-oidc-registered-register"
              >
                {t('auth.noActiveMembershipRegisterCta')}
              </Link>
            </div>
          </div>
        ) : null}

        {oidcRecoveryMessage !== undefined ? (
          <div className="mb-3">
            <AuthFormErrorBanner
              message={oidcRecoveryMessage}
              testId="customer-sign-in-oidc-recovery"
            />
          </div>
        ) : null}

        {currentTenantProspect ? (
          <p
            className="mb-3 text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]"
            data-testid="customer-sign-in-prospect-helper"
          >
            {t('auth.prospectHelper')}{' '}
            <Link
              to={resolveCustomerSignUpPath(tenantCode)}
              className="text-[var(--color-accent)] underline"
              data-testid="customer-sign-in-prospect-register"
            >
              {t('auth.createAccountLink')}
            </Link>
          </p>
        ) : null}

        {accountLocked ? (
          <div
            className="mb-3 rounded-[var(--vcl-control-radius-md)] border border-[var(--color-danger)]/40 bg-[var(--color-danger)]/5 p-3 text-[length:var(--vcl-type-body-sm-size)]"
            role="alert"
            data-testid="customer-sign-in-account-locked"
          >
            <p className="font-medium text-[var(--color-on-surface)]">
              {t('auth.accountLockedTitle')}
            </p>
            <p className="mt-1 text-[var(--color-on-surface-muted)]">
              {accountLockedUntil !== undefined
                ? t('auth.accountLockedUntilBody', {
                    lockedUntil: new Date(accountLockedUntil).toLocaleString(),
                  })
                : t('auth.accountLockedBody')}
            </p>
          </div>
        ) : null}

        {showPageTurnstile
          ? renderTurnstileExecuteField(turnstile, 'customer-sign-in-page-turnstile')
          : null}

        {socialAuth.showAny ? (
          <div className="mb-3 flex flex-col gap-[var(--vcl-space-stack-sm)]">
            {showOidcStartConsents ? (
              <div data-testid="customer-sign-in-oidc-consents">
                <FormErrorSummary errors={oidcConsentSummary} />
                <RegistrationConsentsField
                  value={otpConsents}
                  onChange={setOtpConsents}
                  includeOptional={false}
                  termsInputId={CUSTOMER_SIGN_IN_FIELD_IDS.terms}
                  privacyInputId={CUSTOMER_SIGN_IN_FIELD_IDS.privacy}
                  termsError={consentFieldErrors['consents.termsAccepted']}
                  privacyError={consentFieldErrors['consents.privacyAccepted']}
                />
              </div>
            ) : null}
            <OidcSocialButtons
              t={t}
              showGoogle={socialAuth.showGoogle}
              showApple={socialAuth.showApple}
              disabled={
                requestOtp.isPending || verifyOtp.isPending || loginPassword.isPending
              }
              onGoogle={() => {
                void handleOidcLogin('google');
              }}
              onApple={() => {
                void handleOidcLogin('apple');
              }}
            />
          </div>
        ) : null}

        <div className={CUSTOMER_AUTH_METHOD_TOGGLE_CLASS}>
          <Button
            intent={authMethod === 'password' ? 'primary' : 'secondary'}
            size="lg"
            onClick={switchToPasswordTab}
            data-testid="customer-sign-in-password-tab"
            className="w-full max-w-none whitespace-nowrap"
          >
            {t('auth.passwordTab')}
          </Button>
          <Button
            intent={authMethod === 'otp' ? 'primary' : 'secondary'}
            size="lg"
            onClick={switchToOtpTab}
            data-testid="customer-sign-in-otp-tab"
            className="w-full max-w-none whitespace-nowrap"
          >
            {t('auth.otpTab')}
          </Button>
        </div>

        {renderMainForm(props)}

        <p className="mt-4 text-center text-[length:var(--vcl-type-body-sm-size)] text-[var(--color-on-surface-muted)]">
          {t('auth.noAccount')}{' '}
          <Link
            to={resolveCustomerSignUpPath(tenantCode)}
            className="text-[var(--color-accent)] underline"
            data-testid="customer-sign-in-create-account"
          >
            {t('auth.createAccountLink')}
          </Link>
        </p>
      </Card>
    </div>
  );
}
